import { Trans } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import InlineText from '@/design_system/Typography/InlineText';
import { AmountWithConversions } from '@/models/transaction';
import { createBEMClasses } from '@/utils/classname';
import { formatCurrency } from '@/utils/number';

import { ConversionTooltip } from '../PriceWithDetails/PriceWithDetails';

import '../PriceWithDetails/PriceWithDetails.css';

const { element } = createBEMClasses('price-with-details');

type AmountWithConversionsWithType = AmountWithConversions & {
  isRefund: boolean;
};

export const AmountPaidWithDetailsTooltipWithConversions = ({
  amountPaid,
  transactions,
  size = 'medium',
  variant = 'primary',
  direction = 'row',
  tooltipDirection = 'row',
  gap = '0.5rem',
}: {
  amountPaid: AmountWithConversions;
  transactions: AmountWithConversionsWithType[];
  size?: 'x-small' | 'small' | 'medium';
  variant?: 'primary' | 'secondary' | 'warning';
  direction?: 'row' | 'row-reverse';
  tooltipDirection?: 'row' | 'row-reverse';
  gap?: string;
}) => {
  return (
    <Stack
      style={{
        flexDirection: direction,
        display: 'inline-flex',
        flexWrap: 'nowrap',
      }}
      gap={gap}
    >
      <AmountPaidTooltip
        amountPaid={amountPaid}
        transactions={transactions}
        size={size}
        variant={variant}
      />
      {amountPaid.conversions.map((paidConversion) => {
        const currency = paidConversion.rate.to;
        const transactionsConversions = transactions.map((transaction) =>
          transaction.conversions.find((c) => c.rate.to === currency)
        );

        if (transactionsConversions.some((c) => !c)) {
          return null;
        }

        return (
          <Stack
            key={currency}
            style={{
              color: 'var(--color-text-secondary-black)',
              flexDirection: direction,
            }}
            gap={gap}
            alignItems="center"
          >
            <InlineText color="disabled">|</InlineText>
            <Stack
              style={{ flexDirection: tooltipDirection }}
              alignItems="center"
              gap="0.25rem"
              flexWrap="nowrap"
            >
              <ConversionTooltip conversionRate={paidConversion.rate} />
              <AmountPaidTooltip
                amountPaid={{
                  amount: paidConversion.amount,
                  currency,
                  conversions: [],
                }}
                transactions={transactionsConversions.map((conversion, index) => ({
                  amount: conversion!.amount,
                  currency,
                  conversions: [],
                  isRefund: transactions[index].isRefund,
                }))}
                size={size}
                variant="secondary"
              />
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
};

const AmountPaidTooltip = ({
  amountPaid,
  transactions,
  size,
  variant,
}: {
  amountPaid: AmountWithConversions;
  transactions: AmountWithConversionsWithType[];
  size: 'x-small' | 'small' | 'medium';
  variant: 'primary' | 'secondary' | 'warning';
}) => {
  const inlineTextSize = size === 'x-small' ? '200' : '100';
  const transactionsWithIndexes = getTransactionsWithIndexes(transactions);

  return (
    <Tooltip
      variant="light"
      size="medium"
      hasOverlayArrow={false}
      textAlign="start"
      content={
        <table className={element('table')}>
          <tbody>
            {transactionsWithIndexes.map((transaction, index) => (
              <tr key={index} className={element('row')}>
                <td>
                  <InlineText size="300" color="secondary">
                    {transaction.isRefund ? (
                      <Trans id="amount-paid-with-details.rows.refund">
                        Refund #{transaction.rowIndex}
                      </Trans>
                    ) : (
                      <Trans id="amount-paid-with-details.rows.payment">
                        Payment #{transaction.rowIndex}
                      </Trans>
                    )}
                  </InlineText>
                </td>
                <td>
                  <InlineText size="300" color="secondary">
                    {formatCurrency(transaction.amount, transaction.currency)}
                  </InlineText>
                </td>
              </tr>
            ))}
            <tr className={element('separator')}>
              <td colSpan={2} />
            </tr>
            <tr className={element('row', { 'first-row-of-section': true })}>
              <td>
                <InlineText size="200" weight="medium">
                  {amountPaid.amount < 0 ? (
                    <Trans id="amount-paid-with-details.rows.total-refunded">Total refunded</Trans>
                  ) : (
                    <Trans id="amount-paid-with-details.rows.total-paid">Total paid</Trans>
                  )}
                </InlineText>
              </td>
              <td>
                <InlineText size="200" weight="medium">
                  {formatCurrency(Math.abs(amountPaid.amount), amountPaid.currency)}
                </InlineText>
              </td>
            </tr>
          </tbody>
        </table>
      }
    >
      <Button variant="style-less">
        <InlineText
          size={inlineTextSize}
          weight={size === 'x-small' ? 'regular' : 'medium'}
          color={variant}
          className={element('trigger', { size })}
        >
          {formatCurrency(Math.abs(amountPaid.amount), amountPaid.currency)}
        </InlineText>
      </Button>
    </Tooltip>
  );
};

const getTransactionsWithIndexes = (transactions: AmountWithConversionsWithType[]) => {
  let paymentIndex = 0;
  let refundIndex = 0;

  return transactions.map((transaction) => {
    if (transaction.isRefund) {
      refundIndex += 1;
    } else {
      paymentIndex += 1;
    }

    return {
      ...transaction,
      rowIndex: transaction.isRefund ? refundIndex : paymentIndex,
    };
  });
};
